import { FormControl, InputLabel, ListSubheader, MenuItem, Select as MuiSelect, SelectProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { groupBy, prop } from '../../../util'

interface Option {
  disabled?: boolean
  group?: string
  name: string
  // Shown as a second line on the row when `disabled` is true. Disabled MUI MenuItems don't fire
  // hover events, so this can't be a tooltip - it has to render inline (see PX-1807).
  reason?: string
  value: any
}

type Props = SelectProps & {
  label: string
  name: string
  options: Option[]
  value: string | undefined
}

const reasonStyle = { fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: 'normal' } as const

const makeToOption =
  (selectedValue: any) =>
  ({ disabled, name, reason, value }: Option) =>
    disabled ? (
      <MenuItem key={value} disabled selected={value === selectedValue} value={value}>
        <div>
          <div>{name}</div>
          <div style={reasonStyle}>{reason}</div>
        </div>
      </MenuItem>
    ) : (
      <MenuItem key={value} selected={value === selectedValue} value={value}>
        {name}
      </MenuItem>
    )

const GroupText = styled(ListSubheader)`
  font-size: 0.7rem;
  letter-spacing: 1px;
  padding-left: 0.75rem;
  text-transform: uppercase;
`

const renderOptions = (options: Option[], groupings: Record<string, Option[]>, selectedValue?: string) => {
  const hasGroupings = Object.keys(groupings).length > 0

  if (!hasGroupings) {
    return options.map(makeToOption(selectedValue))
  }

  return Object.keys(groupings).map((key) => [<GroupText key={key}>{key}</GroupText>, groupings[key].map(makeToOption(selectedValue))])
}

const Select = forwardRef(({ defaultValue, disabled, label, name, onChange, options, value, ...props }: Props, ref) => {
  const { t } = useTranslation()
  const labelId = `${name}-label`
  const nonEmptyGroupOptions = options?.filter(({ group }) => group) ?? ([] as Option[])
  // @ts-expect-error We are certain that group is not undefined
  const groupings = groupBy(nonEmptyGroupOptions, prop('group'))
  // MUI's default renderValue clones the matched MenuItem's children into the closed field, which
  // would show the disabled option's stacked name+reason block if the current value happens to
  // match a disabled option (e.g. a value chosen before it became disabled) - always show plain
  // text instead. Falls back to the empty-options placeholder (matching the MenuItem rendered
  // below) or '' rather than the raw value, since a non-matching/still-loading value would
  // otherwise render a raw id or "[object Object]" in the closed field.
  const renderValue = (selectedValue: unknown) => {
    if (!options?.length) return t('pages.appointment.messages.noAvailableSpecialists')
    return options.find((option) => option.value === selectedValue)?.name ?? ''
  }

  return (
    <FormControl variant="standard" sx={{ minWidth: '100%' }}>
      <InputLabel error={props.error} id={labelId}>
        {label}
      </InputLabel>
      <MuiSelect
        {...props}
        disabled={disabled}
        id={name}
        defaultValue={defaultValue ?? ''}
        displayEmpty
        label={label}
        labelId={labelId}
        onChange={onChange}
        name={name}
        ref={ref}
        renderValue={renderValue}
        value={value}
      >
        {options?.length ? (
          renderOptions(options, groupings, value)
        ) : (
          <MenuItem value="">
            <em>{t('pages.appointment.messages.noAvailableSpecialists')}</em>
          </MenuItem>
        )}
      </MuiSelect>
    </FormControl>
  )
})

export { Select }
